/**
 * ProjectFinanceDialog — Evidența financiară a unui proiect.
 * Afișează: Total facturat (din toate facturile, inclusiv externe din SL),
 * Total plătit (suma plăților înregistrate), Rest de plată + bară de progres.
 * Antreprenorul adaugă/șterge plăți (dată, sumă, notă).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DollarSign, Plus, Trash2, Loader2, TrendingUp, Wallet, Receipt } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) =>
  (Number(n) || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ProjectFinanceDialog({ open, onOpenChange, companyId, site }) {
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const siteId = site?.id;

  const fetchFinance = useCallback(async () => {
    if (!siteId || !companyId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${siteId}/finance`, { params: { company_id: companyId } });
      setFinance(res.data);
    } catch (e) {
      toast.error("Nu s-au putut încărca datele financiare");
    } finally {
      setLoading(false);
    }
  }, [siteId, companyId]);

  useEffect(() => {
    if (open) fetchFinance();
  }, [open, fetchFinance]);

  const handleAdd = async () => {
    const amt = parseFloat(String(amount).replace(",", "."));
    if (!amt || amt <= 0) {
      toast.error("Introdu o sumă validă");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/projects/${siteId}/payments?company_id=${companyId}`, {
        amount: String(amount),
        date,
        note,
      });
      setFinance(res.data.finance);
      setAmount("");
      setNote("");
      toast.success("Plată înregistrată");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la înregistrarea plății");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (paymentId) => {
    try {
      const res = await axios.delete(`${API}/projects/${siteId}/payments/${paymentId}?company_id=${companyId}`);
      setFinance(res.data.finance);
      toast.success("Plată ștearsă");
    } catch (e) {
      toast.error("Eroare la ștergere");
    }
  };

  const progress = finance?.progress_percent || 0;
  const payments = finance?.payments || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="project-finance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-700">
            <DollarSign className="w-5 h-5" />
            Financiar — {site?.name}
          </DialogTitle>
          <DialogDescription>
            Evidența plăților încasate raportat la totalul facturat (inclusiv facturile externe din Situații de Lucrări).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-green-600" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                <p className="text-[11px] text-slate-500 flex items-center gap-1"><Receipt className="w-3 h-3" />Facturat</p>
                <p className="text-base font-bold text-slate-800" data-testid="finance-total-facturat">{fmt(finance?.total_facturat)}</p>
              </div>
              <div className="rounded-lg border border-green-100 bg-green-50/50 p-3">
                <p className="text-[11px] text-slate-500 flex items-center gap-1"><Wallet className="w-3 h-3" />Plătit</p>
                <p className="text-base font-bold text-green-700" data-testid="finance-total-platit">{fmt(finance?.total_platit)}</p>
              </div>
              <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3">
                <p className="text-[11px] text-slate-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Rest</p>
                <p className="text-base font-bold text-amber-700" data-testid="finance-rest">{fmt(finance?.rest_de_plata)}</p>
              </div>
            </div>

            {/* Progress bar */}
            <div>
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Progres încasare</span>
                <span className="font-semibold text-green-700">{progress}%</span>
              </div>
              <div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all duration-500"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                  data-testid="finance-progress-bar"
                />
              </div>
              <p className="text-[11px] text-slate-400 mt-1">
                {finance?.numar_facturi || 0} factur{(finance?.numar_facturi || 0) === 1 ? "ă" : "i"} în acest proiect
              </p>
            </div>

            {/* Add payment */}
            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                <Plus className="w-4 h-4 text-green-600" />Înregistrează o plată
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Sumă (RON)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="ex: 5000"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    data-testid="payment-amount-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Data</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="payment-date-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notă (opțional)</Label>
                <Input placeholder="ex: avans, tranșa 1..." value={note} onChange={(e) => setNote(e.target.value)} data-testid="payment-note-input" />
              </div>
              <Button
                onClick={handleAdd}
                disabled={saving}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="payment-add-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Adaugă plată
              </Button>
            </div>

            {/* Payments list */}
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">Plăți înregistrate ({payments.length})</p>
              {payments.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nicio plată înregistrată încă.</p>
              ) : (
                <div className="space-y-2" data-testid="payments-list">
                  {payments.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2"
                      data-testid={`payment-row-${p.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-green-700">{fmt(p.amount)} RON</p>
                        <p className="text-xs text-slate-500 truncate">
                          {p.date}{p.note ? ` · ${p.note}` : ""}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(p.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`payment-delete-${p.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
